// Cattle breeding timeline helpers. Verbatim extract from main.jsx.
import {addDays, toISO} from './dateUtils.js';
import {
  CATTLE_BULL_EXPOSURE_DAYS,
  CATTLE_PREG_CHECK_OFFSET_DAYS,
  CATTLE_GESTATION_DAYS,
  CATTLE_CALVING_WINDOW_DAYS,
  CATTLE_NURSING_DAYS,
} from './cattle.js';

export type CattleBreedingTimeline = {
  exposureStart: string;
  exposureEnd: string;
  pregCheckDate: string;
  calvingStart: string;
  calvingEnd: string;
  weaningDate: string;
};

// The persisted cattle_breeding_cycles row, narrowed to the two columns this
// module reads. Both are optional because a freshly drafted cycle can be saved
// without a bull-exposure date, which is exactly what buildCattleCycleSeqMap
// filters out below.
export type CattleBreedingCycleRow = {
  id?: string | number | null;
  bull_exposure_start?: string | null;
};

// A cycle that survived the `dated` filter: it definitely has both fields.
type DatedCattleBreedingCycle = CattleBreedingCycleRow & {id: string | number; bull_exposure_start: string};

export function calcCattleBreedingTimeline(exposureStart: string | null | undefined): CattleBreedingTimeline | null {
  if (!exposureStart) return null;
  const d0 = new Date(exposureStart + 'T12:00:00');
  const exposureEnd = toISO(addDays(d0, CATTLE_BULL_EXPOSURE_DAYS - 1));
  const pregCheckDate = toISO(addDays(d0, CATTLE_BULL_EXPOSURE_DAYS - 1 + CATTLE_PREG_CHECK_OFFSET_DAYS));
  const calvingStart = toISO(addDays(d0, CATTLE_GESTATION_DAYS));
  const calvingEnd = toISO(addDays(d0, CATTLE_GESTATION_DAYS + CATTLE_CALVING_WINDOW_DAYS - 1));
  const weaningDate = toISO(addDays(d0, CATTLE_GESTATION_DAYS + CATTLE_NURSING_DAYS - 1));
  return {exposureStart, exposureEnd, pregCheckDate, calvingStart, calvingEnd, weaningDate};
}

// Per-year global sequence map for cattle cycles. Format: "YY-NN".
// Parallels buildCycleSeqMap for pigs but keyed off bull_exposure_start.
export function buildCattleCycleSeqMap(
  cycles: readonly CattleBreedingCycleRow[] | null | undefined,
): Record<string, string> {
  const seqMap: Record<string, string> = {};
  // Same truthiness test, now stated as a type predicate so the sort and the
  // slice below can read both fields without re-proving they are present.
  const dated = (cycles || []).filter((c): c is DatedCattleBreedingCycle => !!(c && c.id && c.bull_exposure_start));
  const sorted = [...dated].sort((a, b) => {
    if (a.bull_exposure_start !== b.bull_exposure_start)
      return a.bull_exposure_start.localeCompare(b.bull_exposure_start);
    return String(a.id).localeCompare(String(b.id));
  });
  const yearCounts: Record<string, number> = {};
  sorted.forEach((c) => {
    const yr = c.bull_exposure_start.slice(2, 4);
    yearCounts[yr] = (yearCounts[yr] || 0) + 1;
    seqMap[c.id] = yr + '-' + String(yearCounts[yr]).padStart(2, '0');
  });
  return seqMap;
}
export function cattleCycleLabel(
  cycle: CattleBreedingCycleRow | null | undefined,
  seqMap: Record<string, string> | null | undefined,
): string {
  if (!cycle) return '';
  // `cycle.id != null` replaces an implicit `seqMap[undefined]` lookup, which
  // returned undefined and fell through to the unsuffixed label — the same
  // result this explicit check produces.
  const suffix = seqMap && cycle.id != null ? seqMap[cycle.id] : null;
  return 'Cattle Cycle' + (suffix ? ' ' + suffix : '');
}

// ── AUTO-STATUS HELPERS ──────────────────────────────────────────────────
